import { initializeApp } from 'firebase/app'
import {
  deleteToken,
  getMessaging,
  getToken,
  type MessagePayload,
  type Messaging,
  onMessage
} from 'firebase/messaging'
import { useUserStore } from '@/stores/user'
import { UserService } from '@/services/telehealthApi/UserService'
import { useNoticeStore } from '@/stores/notice'
import { NoticeType } from '@/stores/models/Notice'
import { urlSafeBase64 } from '@/util/base64'
import {
  NotificationAuditApi,
  NotificationAuditRequestAppStateEnum,
  NotificationAuditRequestEventTypeEnum,
  NotificationAuditRequestPlatformEnum,
  UserRightsEnum
} from '@/generated'
import { useConsultationRequestStore } from '@/stores/consultationRequest'
import { globalApiConfig } from '@/services/telehealthApi/APIConfig'
import useLocalStoragePerUser, {
  PUSH_NOTIFICATIONS_ACTIVE_KEY
} from '@/composables/useLocalStoragePerUser'
import { storeToRefs } from 'pinia'
import { usePluginsStore } from '@/stores/plugins'

export class PushNotificationService {
  static lastIncomingRefreshAt = 0
  private static readonly INCOMING_REFRESH_THROTTLE_MS = 2000
  static messaging: Messaging | undefined = undefined

  private static consultationRefreshTriggers = [
    'URGENT consultation request',
    'EMERGENCY consultation request',
    'ROUTINE consultation request',
    'Consultation in progress',
    'DRINGLICHE Konsilanfrage',
    'NOTFALL-Konsilanfrage',
    'ROUTINE-Konsilanfrage',
    'Konsultation läuft'
  ]

  // Send device token to Service Worker
  // SW persists it in IndexedDB to survive SW restarts
  private static async sendTokenToServiceWorker(token: string | null): Promise<void> {
    try {
      if (!('serviceWorker' in navigator)) return

      const registration = await navigator.serviceWorker.getRegistration()
      const sw = registration?.active

      if (sw) {
        sw.postMessage({ type: 'SET_DEVICE_TOKEN', token })
      } else {
        console.warn('[PushNotificationService] No active Service Worker found to send token to.')
      }
    } catch (error) {
      console.error(
        '[PushNotificationService] Failed to send device token to Service Worker:',
        error
      )
    }
  }

  // Send audit log via generated OpenAPI client
  private static async reportAuditLog(
    notificationId: string,
    eventType: NotificationAuditRequestEventTypeEnum,
    appState: NotificationAuditRequestAppStateEnum,
    deviceToken?: string
  ): Promise<void> {
    try {
      const auditApi = new NotificationAuditApi(globalApiConfig)
      await auditApi.logNotificationEvent({
        notificationId,
        eventType,
        platform: NotificationAuditRequestPlatformEnum.WEB_BROWSER,
        appState,
        ...(deviceToken ? { deviceToken } : {})
      })
    } catch (e) {
      console.error(
        `[PushNotificationService] Failed to send ${eventType} audit log for notification: ${notificationId}`,
        e
      )
    }
  }

  // Initialize Firebase and register Service Worker
  static async initFirebase(forceRefresh: boolean = true): Promise<void> {
    try {
      const firebaseOptions = useUserStore().firebaseOptions
      if (firebaseOptions === undefined) {
        console.error(
          '[PushNotificationService] Firebase options are undefined. Cannot initialize.'
        )
        return
      }
      console.debug('[PushNotificationService] Firebase options retrieved successfully.')

      if (!('serviceWorker' in navigator)) {
        console.warn(
          '[PushNotificationService] Requirements not met: Service Workers are not supported in this browser.'
        )
        useNoticeStore().add({
          type: NoticeType.WARNING,
          message: 'Requirements not met for push notifications.'
        })
        return
      }

      useUserStore().setNotificationTokenLoadingState('loading')

      const registration = await navigator.serviceWorker.register(
        `${import.meta.env.BASE_URL}firebase-messaging-sw.js?config=${urlSafeBase64(JSON.stringify(firebaseOptions))}`,
        { type: 'classic', scope: import.meta.env.BASE_URL }
      )

      const firebaseApp = initializeApp(firebaseOptions)
      PushNotificationService.messaging = getMessaging(firebaseApp)

      if (forceRefresh) {
        try {
          await deleteToken(PushNotificationService.messaging)
        } catch (error) {
          console.warn('[PushNotificationService] Error during token deletion:', error)
        }
      }

      const currentToken = await getToken(PushNotificationService.messaging, {
        vapidKey: useUserStore().firebaseOptions?.vapidKey,
        serviceWorkerRegistration: registration
      }).catch((error) => {
        console.warn('[PushNotificationService] Warning: getToken failed temporarily:', error)
        return undefined
      })

      if (!currentToken) {
        console.warn(
          '[PushNotificationService] No registration token available. Push notifications deactivated.'
        )
        useUserStore().setNotificationTokenLoadingState('deactivated')
        return
      }

      useUserStore().setNotificationToken(currentToken)
      await UserService.login(useUserStore().user?.userName ?? '', {
        notificationToken: currentToken
      })

      // Persist token in SW IndexedDB so it survives SW restarts (e.g. background notifications)
      await PushNotificationService.sendTokenToServiceWorker(currentToken)

      useUserStore().setNotificationTokenLoadingState('success')

      console.log('[PushNotificationService] Setting up foreground message listener (onMessage)...')

      onMessage(PushNotificationService.messaging, async (payload: MessagePayload) => {
        const notificationId = payload.data?.notificationId
        if (notificationId) {
          await PushNotificationService.reportAuditLog(
            notificationId.toString(),
            NotificationAuditRequestEventTypeEnum.RECEIVED,
            NotificationAuditRequestAppStateEnum.FOREGROUND,
            useUserStore().notificationToken
          )
        } else {
          console.warn(
            '[PushNotificationService] No notificationId found in payload.data. Audit log skipped for RECEIVED event.'
          )
        }

        await PushNotificationService.handleIncomingPushPayload(payload)

        if (!('Notification' in window)) {
          console.error(
            '[PushNotificationService] Notification API is not supported by this browser.'
          )
          useNoticeStore().add({
            type: NoticeType.ERROR,
            message: "Your device/browser doesn't support push notifications!"
          })
        } else if (Notification.permission === 'granted') {
          await showNewNotification(registration, payload)
        } else if (Notification.permission !== 'denied') {
          const permission = await Notification.requestPermission()
          if (permission === 'granted') {
            await showNewNotification(registration, payload)
          } else {
            console.warn('[PushNotificationService] Permission denied by user after prompt.')
            useNoticeStore().add({
              type: NoticeType.WARNING,
              message:
                'Push notifications are not allowed yet for e:lmo. Please allow push notifications from your profile or App/Browser'
            })
          }
        } else {
          console.warn(
            '[PushNotificationService] Notification permission is explicitly denied by browser settings.'
          )
        }
      })
    } catch (error) {
      console.error('[PushNotificationService] Error initializing firebase:', error)
    }
  }

  // True when push notifications were previously enabled on this device
  // (persisted in localStorage), the browser permission is still granted, and
  // no FCM token is registered yet in this session — i.e. the token needs to be
  // re-registered after a fresh login / app entry.
  static shouldRestoreRegistration(): boolean {
    const { getUsersByKey } = useLocalStoragePerUser()
    const userName = useUserStore().user?.userName ?? ''

    return (
      typeof window !== 'undefined' &&
      'Notification' in window &&
      useUserStore().notificationToken === undefined &&
      Notification.permission === 'granted' &&
      getUsersByKey(PUSH_NOTIFICATIONS_ACTIVE_KEY).includes(userName)
    )
  }

  // Re-register the FCM token on app entry / login when the user previously
  // enabled push notifications on this device. Safe to call unconditionally — it
  // no-ops when a precondition is not met. Returns true when a (re-)registration
  // was started.
  static async restoreRegistrationFromLocalPreference(): Promise<boolean> {
    if (!PushNotificationService.shouldRestoreRegistration()) {
      return false
    }

    await PushNotificationService.initFirebase(false)
    return true
  }

  // Request notification permission and initialize Firebase
  static async activateNotifications(): Promise<void> {
    const { addUserToLocalStorageByKey } = useLocalStoragePerUser()
    const { isConnectEnabled } = storeToRefs(usePluginsStore())
    const userStore = useUserStore()
    const userRights = userStore.user!.userRights

    if (
      userRights.every(
        (right) => right === UserRightsEnum.HOSPITAL_IT || right === UserRightsEnum.TCC_IT
      ) ||
      !isConnectEnabled.value
    ) {
      return
    }

    try {
      const permission = await Notification.requestPermission()

      if (permission === 'granted') {
        await PushNotificationService.initFirebase()
        addUserToLocalStorageByKey(PUSH_NOTIFICATIONS_ACTIVE_KEY)
      } else {
        console.warn(
          '[PushNotificationService] Notification permission was not granted. Deactivating token loading state.'
        )
        useUserStore().setNotificationTokenLoadingState('deactivated')
        useNoticeStore().add({
          type: NoticeType.WARNING,
          message:
            'Push notifications are not allowed yet for e:lmo. Please allow push notifications from your profile or App/Browser'
        })
      }
    } catch (e) {
      console.error('[PushNotificationService] Exception caught during activateNotifications:', e)
      useUserStore().setNotificationTokenLoadingState('error')
    }
  }

  // Delete FCM token and clear device token from SW IndexedDB.
  // On logout pass { preserveUserPreference: true } so the user's "push active"
  // setting survives and the switch can be restored on the next login.
  static async deactivateNotificationReception(
    options: { preserveUserPreference?: boolean } = {}
  ): Promise<void> {
    const { removeUserFromLocalStorageByKey } = useLocalStoragePerUser()
    try {
      useUserStore().setNotificationTokenLoadingState('loading')

      if (PushNotificationService.messaging !== undefined) {
        await deleteToken(PushNotificationService.messaging)
      } else {
        console.debug(
          '[PushNotificationService] No active messaging instance found to delete token from.'
        )
      }

      if (useUserStore().notificationToken !== undefined) {
        await UserService.logoutDevice(useUserStore().user?.userName ?? '', {
          notificationToken: useUserStore().notificationToken ?? ''
        })
        useUserStore().clearNotificationToken()
      }

      // Clear device token from SW IndexedDB to prevent stale token usage after logout
      await PushNotificationService.sendTokenToServiceWorker(null)
      if (!options.preserveUserPreference) {
        removeUserFromLocalStorageByKey(PUSH_NOTIFICATIONS_ACTIVE_KEY)
      }
      useUserStore().setNotificationTokenLoadingState('deactivated')
    } catch (error) {
      console.error(
        '[PushNotificationService] Error while deactivating notification reception:',
        error
      )
    }
  }

  // Refresh consultation list on matching push payload
  static async handleIncomingPushPayload(payload: MessagePayload): Promise<void> {
    const now = Date.now()
    if (
      now - PushNotificationService.lastIncomingRefreshAt <
      PushNotificationService.INCOMING_REFRESH_THROTTLE_MS
    ) {
      return
    }

    const title = (payload?.data?.title ?? '').toString().trim()

    const shouldRefresh = PushNotificationService.consultationRefreshTriggers.some((t) =>
      title.includes(t)
    )

    if (shouldRefresh) {
      PushNotificationService.lastIncomingRefreshAt = now
      await useConsultationRequestStore().refreshIncomingConsultationRequestList()
    }
  }
}

// Show native notification via Service Worker registration
async function showNewNotification(
  registration: ServiceWorkerRegistration,
  payload: MessagePayload
): Promise<void> {
  if (!payload.data || !payload.data.title) {
    console.warn(
      '[PushNotificationService] showNewNotification aborted: Missing payload.data or payload.data.title',
      payload
    )
    return
  }

  await registration.showNotification(payload.data.title, {
    body: payload.data.text,
    icon: `${import.meta.env.BASE_URL}icon-192x192.png`,
    data: {
      url: payload.data.link,
      notificationId: payload.data.notificationId
    }
  })
}
